import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { useDevotionalSeries, type DevotionalSeries } from '../hooks/useDevotionalSeries';
import { useDevotionals } from '../hooks/useDevotionals';
import { useUpsertDevotional } from '../hooks/useUpsertDevotional';
import { supabase } from '../services/supabase';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const FormCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  padding: 1.5rem;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  max-width: 900px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Input = styled.input`
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
`;

const Textarea = styled.textarea`
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
`;

const Select = styled.select`
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
`;

const Button = styled.button`
  background-color: var(--primary);
  color: #fff;
  padding: 0.6rem 1.1rem;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  box-shadow: 0 10px 18px rgba(31, 117, 254, 0.25);
  &:disabled {
    opacity: 0.6;
    cursor: default;
    box-shadow: none;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  max-width: 900px;

  h1 {
    margin: 0;
  }
`;

const UploadButton = styled.span`
  display: inline-flex;
  align-items: center;
  background: var(--surface);
  color: var(--onSurface);
  padding: 0.55rem 0.9rem;
  border-radius: 12px;
  border: 1px solid var(--outline);
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: var(--surfaceVariant);
    border-color: var(--primary);
    color: var(--primary);
  }
`;

const DEVOTIONAL_DAYS_TOTAL = 6;
const dayLabels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const parseDevotionalContent = (content: string | null | undefined) => {
  const safeContent = content ?? '';
  const dayContents = Array(DEVOTIONAL_DAYS_TOTAL).fill('');

  if (!safeContent.trim()) {
    return { dayContents, rawContent: '' };
  }

  const dayRegex = /(?:^|\n)\s*day\s*(\d+)\s*[:-]/gi;
  const matches: RegExpExecArray[] = [];
  let match = dayRegex.exec(safeContent);
  while (match) {
    matches.push(match);
    match = dayRegex.exec(safeContent);
  }

  if (matches.length > 0) {
    matches.forEach((match, index) => {
      const dayNumber = Number(match[1]);
      if (!Number.isFinite(dayNumber) || dayNumber < 1 || dayNumber > DEVOTIONAL_DAYS_TOTAL) {
        return;
      }
      const start = match.index ?? 0;
      const end = index + 1 < matches.length ? matches[index + 1].index ?? safeContent.length : safeContent.length;
      const section = safeContent.slice(start, end).replace(dayRegex, '').trim();
      dayContents[dayNumber - 1] = section;
    });
    return { dayContents, rawContent: '' };
  }

  const dividerSplit = safeContent
    .split('\n\n---\n\n')
    .map((section) => section.trim())
    .filter(Boolean);
  if (dividerSplit.length === DEVOTIONAL_DAYS_TOTAL) {
    return { dayContents: dividerSplit, rawContent: '' };
  }

  return { dayContents, rawContent: safeContent };
};

const buildDayContent = (days: string[]) =>
  days
    .map((text, index) => {
      const trimmed = text.trim();
      return trimmed ? `Day ${index + 1}:\n${trimmed}` : `Day ${index + 1}:`;
    })
    .join('\n\n')
    .trim();

type DevotionalJsonExtras = {
  author?: string;
  reading_time?: string;
  tags?: string[];
};

type ParsedDevotionalWeek = {
  title: string;
  subtitle: string;
  date: string;
  thumbnail: string;
  seriesId: string | null;
  seriesTitle: string | null;
  dayTitles: string[];
  dayContents: string[];
  extras: DevotionalJsonExtras;
};

const toTrimmedString = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const parseDevotionalWeekJson = (raw: unknown): ParsedDevotionalWeek => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error('Devotional JSON must be an object.');
  }

  const input = raw as Record<string, unknown>;
  const title = toTrimmedString(input.title);
  if (!title) {
    throw new Error('Devotional title is required.');
  }

  const seriesId = toTrimmedString(input.series_id) || null;
  const seriesTitle = toTrimmedString(input.series_title) || null;
  if (!seriesId && !seriesTitle) {
    throw new Error('Devotional JSON must include series_id or series_title.');
  }

  if (!Array.isArray(input.days)) {
    throw new Error('Devotional JSON must include a "days" array.');
  }
  if (input.days.length !== DEVOTIONAL_DAYS_TOTAL) {
    throw new Error(`Devotional JSON must include exactly ${DEVOTIONAL_DAYS_TOTAL} days.`);
  }

  const dayTitles: string[] = [];
  const dayContents: string[] = [];
  input.days.forEach((entry, index) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      throw new Error(`Day ${index + 1} must be an object.`);
    }
    const day = entry as Record<string, unknown>;
    const dayContent = toTrimmedString(day.content);
    if (!dayContent) {
      throw new Error(`Day ${index + 1} content is required.`);
    }
    dayTitles.push(toTrimmedString(day.title));
    dayContents.push(dayContent);
  });

  const dateValue = toTrimmedString(input.date);
  const date = /^\d{4}-\d{2}-\d{2}$/.test(dateValue)
    ? dateValue
    : new Date().toISOString().slice(0, 10);

  const extras: DevotionalJsonExtras = {};
  const author = toTrimmedString(input.author);
  if (author) extras.author = author;
  const readingTime = toTrimmedString(input.reading_time);
  if (readingTime) extras.reading_time = readingTime;
  if (Array.isArray(input.tags)) {
    extras.tags = input.tags
      .filter((entry): entry is string => typeof entry === 'string')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  return {
    title,
    subtitle: toTrimmedString(input.subtitle),
    date,
    thumbnail: toTrimmedString(input.thumbnail),
    seriesId,
    seriesTitle,
    dayTitles,
    dayContents,
    extras,
  };
};

const findSeriesId = (
  seriesList: DevotionalSeries[] | undefined,
  seriesId: string | null,
  seriesTitle: string | null,
): string | null => {
  const list = seriesList ?? [];
  if (seriesId) {
    const byId = list.find((series) => series.id === seriesId);
    if (byId) return byId.id;
  }
  if (seriesTitle) {
    const needle = seriesTitle.toLowerCase();
    const byTitle = list.find((series) => series.title.trim().toLowerCase() === needle);
    if (byTitle) return byTitle.id;
  }
  return null;
};

export const DevotionalFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: seriesList } = useDevotionalSeries();
  const { data: devotionals } = useDevotionals();
  const existing = devotionals?.find((d) => d.id === id);

  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [date, setDate] = useState('');
  const [seriesId, setSeriesId] = useState<string | ''>('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [dayTitles, setDayTitles] = useState<string[]>(
    () => Array(DEVOTIONAL_DAYS_TOTAL).fill('')
  );
  const [dayContents, setDayContents] = useState<string[]>(
    () => Array(DEVOTIONAL_DAYS_TOTAL).fill('')
  );
  const [content, setContent] = useState('');
  const [loadingContent, setLoadingContent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [jsonExtras, setJsonExtras] = useState<DevotionalJsonExtras | null>(null);

  useEffect(() => {
    if (existing) {
      setTitle(existing.title);
      setSubtitle(existing.subtitle ?? '');
      setDate(existing.date ?? '');
      setSeriesId(existing.series_id ?? '');
      setThumbnailUrl(existing.thumbnail ?? '');
    }
  }, [existing]);

  useEffect(() => {
    if (!id) return;
    let mounted = true;
    const loadContent = async () => {
      setLoadingContent(true);
      const { data, error } = await supabase
        .from('devotionals')
        .select('content, day_titles')
        .eq('id', id)
        .maybeSingle();
      if (!error && data && mounted) {
        const parsed = parseDevotionalContent(data.content);
        setDayContents(parsed.dayContents);
        setContent(parsed.rawContent);
        if (Array.isArray(data.day_titles)) {
          const nextTitles = Array(DEVOTIONAL_DAYS_TOTAL).fill('');
          data.day_titles.forEach((title: string, index: number) => {
            if (index < DEVOTIONAL_DAYS_TOTAL) nextTitles[index] = title ?? '';
          });
          setDayTitles(nextTitles);
        }
      }
      if (mounted) {
        setLoadingContent(false);
      }
    };
    loadContent();
    return () => {
      mounted = false;
    };
  }, [id]);

  const upsert = useUpsertDevotional();

  const handleJsonUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setJsonError(null);
    try {
      const text = await file.text();
      let raw: unknown;
      try {
        raw = JSON.parse(text);
      } catch {
        throw new Error('Failed to parse JSON.');
      }

      const parsed = parseDevotionalWeekJson(raw);
      const matchedId = findSeriesId(seriesList, parsed.seriesId, parsed.seriesTitle);
      setTitle(parsed.title);
      setSubtitle(parsed.subtitle);
      setDate(parsed.date);
      setThumbnailUrl(parsed.thumbnail);
      setDayTitles(parsed.dayTitles);
      setDayContents(parsed.dayContents);
      setContent('');
      setFormError(null);
      setJsonExtras(Object.keys(parsed.extras).length > 0 ? parsed.extras : null);

      if (matchedId) {
        setSeriesId(matchedId);
      } else {
        setSeriesId('');
        const label = parsed.seriesTitle || parsed.seriesId || 'that series';
        setJsonError(`No series matched "${label}". Select a series before saving.`);
      }
    } catch (err: any) {
      setJsonError(err?.message || 'Failed to parse JSON.');
    } finally {
      event.target.value = '';
    }
  };

  const handleUpload = async (file: File) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}.${fileExt}`;
    const { error } = await supabase.storage.from('post-images').upload(fileName, file, { upsert: false });
    if (error) throw error;
    const { data } = supabase.storage.from('post-images').getPublicUrl(fileName);
    setThumbnailUrl(data.publicUrl);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!seriesId) {
      setFormError('Please select a devotional series.');
      return;
    }

    const hasDayContent = dayContents.some((text) => text.trim().length > 0);
    const finalContent = hasDayContent ? buildDayContent(dayContents) : content;
    const safeDate = date || new Date().toISOString().slice(0, 10);
    const cleanedDayTitles = dayTitles.map((title) => title.trim());

    try {
      await upsert.mutateAsync({
        id,
        title,
        subtitle,
        date: safeDate,
        series_id: seriesId || undefined,
        thumbnail: thumbnailUrl || undefined,
        content: finalContent || '',
        day_titles: cleanedDayTitles,
        ...(jsonExtras?.author !== undefined ? { author: jsonExtras.author } : {}),
        ...(jsonExtras?.reading_time !== undefined ? { reading_time: jsonExtras.reading_time } : {}),
        ...(jsonExtras?.tags !== undefined ? { tags: jsonExtras.tags } : {}),
      });
      // Refetch the list so the new/updated devotional is visible when we navigate
      await queryClient.refetchQueries({ queryKey: ['devotionals'] });
      navigate('/devotionals');
    } catch (error: any) {
      const message = error?.message ?? error?.error_description ?? JSON.stringify(error);
      setFormError(message || 'Failed to save devotional.');
    }
  };

  return (
    <Page>
      <HeaderRow>
        <h1>{editing ? 'Edit' : 'New'} Devotional</h1>
        <label>
          <UploadButton>Upload JSON</UploadButton>
          <input
            type="file"
            accept="application/json"
            onChange={handleJsonUpload}
            style={{ display: 'none' }}
          />
        </label>
      </HeaderRow>
      {loadingContent && <p>Loading content…</p>}
      {jsonError && <p style={{ color: 'var(--error)' }}>{jsonError}</p>}
      {formError && <p style={{ color: 'var(--error)' }}>{formError}</p>}
      <FormCard>
        <Form onSubmit={handleSubmit}>
        <label>
          Title
          <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
        </label>
        <label>
          Subtitle
          <Input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
        </label>
        <label>
          Date
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label>
          Series
          <Select value={seriesId} onChange={(e) => setSeriesId(e.target.value)}>
            <option value="">— None —</option>
            {seriesList?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
              </option>
            ))}
          </Select>
        </label>
        {thumbnailUrl && (
          <img
            src={thumbnailUrl}
            alt="Devotional thumbnail preview"
            style={{ width: '100%', objectFit: 'cover', borderRadius: 6 }}
          />
        )}
        <label>
          Upload Thumbnail
          <Input
            type="file"
            accept="image/*"
            onChange={async (e) => {
              if (e.target.files?.[0]) {
                try {
                  await handleUpload(e.target.files[0]);
                } catch (err) {
                  // eslint-disable-next-line no-alert
                  alert('Upload failed');
                }
              }
            }}
          />
        </label>
        <label>
          Content
          <Textarea
            value={content}
            rows={4}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Legacy content (used if daily content is empty)"
          />
        </label>
        <div>
          <strong>Daily Content</strong>
          <p style={{ margin: '0.25rem 0 0.75rem', color: 'var(--onSurfaceVariant)' }}>
            Fill in each day to match the mobile devotional reader.
          </p>
          {dayContents.map((dayContent, index) => (
            <label key={`day-${index}`} style={{ display: 'block', marginBottom: '0.75rem' }}>
              Day {index + 1} {dayLabels[index] ? `(${dayLabels[index]})` : ''}
              <Input
                value={dayTitles[index] ?? ''}
                onChange={(e) =>
                  setDayTitles((prev) => {
                    const next = [...prev];
                    next[index] = e.target.value;
                    return next;
                  })
                }
                placeholder={`Title for Day ${index + 1}`}
                style={{ margin: '0.5rem 0' }}
              />
              <Textarea
                value={dayContent}
                rows={4}
                onChange={(e) =>
                  setDayContents((prev) => {
                    const next = [...prev];
                    next[index] = e.target.value;
                    return next;
                  })
                }
                placeholder={`Content for Day ${index + 1}`}
              />
            </label>
          ))}
        </div>
        <Button type="submit" disabled={upsert.isPending}>
          {upsert.isPending ? 'Saving…' : 'Save'}
        </Button>
        </Form>
      </FormCard>
    </Page>
  );
};

export default DevotionalFormPage;
